import { Formik, FormikContextType, useFormikContext } from 'formik'
import { PropsWithChildren, useCallback, useMemo } from 'react'
import { Employee } from '../../../types/Employee'

interface FormValues {
  firstName: string
  lastName: string
  nationalityCountryId: string | null
  ssn: string
  // -> id card
  idCardNumber: string | null
  idCardExpiresOn: Date | null
  idCardFrontPicture: string | null
  idCardBackPicture: string | null
  // residence permit
  residencePermitExpiresOn: Date | null
  residencePermitCode: string | null
  residencePermitFrontPicture: string | null
  residencePermitBackPicture: string | null
  // work permit
  workPermitExpiresOn: Date | null
  workPermitFrontPicture: string | null
  workPermitBackPicture: string | null
}

export function useForm(): FormikContextType<FormValues> {
  return useFormikContext()
}

interface FormProviderProps {
  employee: Employee
}

export const FormProvider = ({
  employee,
  children
}: PropsWithChildren<FormProviderProps>) => {
  const initialFormValues: FormValues = useMemo(
    () => ({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      nationalityCountryId: employee.profile?.nationalityCountry?.id || null,
      ssn: employee.ssn || '',
      // -> id card
      idCardNumber: employee.profile?.idCardNumber || null,
      idCardExpiresOn: employee.profile?.idCardExpiresOn
        ? new Date(employee.profile.idCardExpiresOn)
        : null,
      idCardFrontPicture: employee.profile?.idCardFrontPicture || null,
      idCardBackPicture: employee.profile?.idCardBackPicture || null,
      // residence permit
      residencePermitFrontPicture:
        employee.workProfile?.residencePermitFrontPicture || null,
      residencePermitBackPicture:
        employee.workProfile?.residencePermitBackPicture || null,
      residencePermitExpiresOn: employee.workProfile?.residencePermitExpiresOn
        ? new Date(employee.workProfile.residencePermitExpiresOn)
        : null,
      residencePermitCode: employee.workProfile?.residencePermitCode || null,
      // ->  work permit
      workPermitFrontPicture:
        employee.workProfile?.workPermitFrontPicture || null,
      workPermitBackPicture:
        employee.workProfile?.workPermitBackPicture || null,
      workPermitExpiresOn: employee.workProfile?.workPermitExpiresOn
        ? new Date(employee.workProfile.workPermitExpiresOn)
        : null
    }),
    [
      employee.firstName,
      employee.lastName,
      employee.profile?.idCardBackPicture,
      employee.profile?.idCardExpiresOn,
      employee.profile?.idCardFrontPicture,
      employee.profile?.idCardNumber,
      employee.profile?.nationalityCountry?.id,
      employee.ssn,
      employee.workProfile?.residencePermitBackPicture,
      employee.workProfile?.residencePermitCode,
      employee.workProfile?.residencePermitExpiresOn,
      employee.workProfile?.residencePermitFrontPicture,
      employee.workProfile?.workPermitBackPicture,
      employee.workProfile?.workPermitExpiresOn,
      employee.workProfile?.workPermitFrontPicture
    ]
  )

  const handleSubmit = useCallback(() => {}, [])

  return (
    <Formik<FormValues>
      initialValues={initialFormValues}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
